/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import { font, weightMedium } from 'src/components/css'

interface BetaBadgeProps {
  /** Extra emotion styles from the call site (spacing, alignment). */
  className?: string
  /**
   * Set when the badge sits ON a `colors.PRIMARY` fill (a selected segmented
   * option, a primary button) — the default PRIMARY badge would vanish into it,
   * so fill and text swap.
   */
  inverse?: boolean
}

/**
 * "BETA" pill, for features still being trialled.
 *
 * Content colour comes from `PRIMARY_BUTTON_CONTENT` rather than a literal:
 * the fill is tenant-switched `colors.PRIMARY` (SportNinja amber, Canlan
 * blue), so the text must switch with it. The font comes from `css.js` for
 * the same reason — Inter on SportNinja, Gilroy on Canlan.
 */
const BetaBadge: React.FC<BetaBadgeProps> = ({
  className,
  inverse = false,
}) => (
  <span className={className} css={[badgeStyle, inverse && inverseStyle]}>
    BETA
  </span>
)

export default BetaBadge

// ---- styled ----
const badgeStyle = css`
  display: inline-flex;
  align-items: center;
  background: ${colors.PRIMARY};
  padding: 2px 6px;
  border-radius: 4px;
  ${font.body}
  ${weightMedium}
  font-size: 10px;
  line-height: 1;
  letter-spacing: 0.04em;
  color: ${PRIMARY_BUTTON_CONTENT.text};
  white-space: nowrap;
  vertical-align: middle;
`

// Swapped: the content colour becomes the fill, PRIMARY the text — the same
// tenant-aware pair, so it stays legible on every PRIMARY.
const inverseStyle = css`
  background: ${PRIMARY_BUTTON_CONTENT.text};
  color: ${colors.PRIMARY};
`
